import {
  getMessaging,
  getToken,
  deleteToken,
  isSupported,
} from 'firebase/messaging';
import { getApps, initializeApp } from 'firebase/app';

import type { PushAPI, PushSubscriptionLike } from '../../types/push-api.types';
import type { FirebaseMessagingAPIOptions } from '../../types/firebase-config.types';

import { ready as swReady, getSubscription } from '../../sw';
import { ERRORS } from '../../settings';
import { describeSubscription } from '../../utils/push-api';
import { PushNotificationsError } from '../../utils/error';

export class FirebaseMessagingAPI
  implements PushAPI<FirebaseMessagingAPIOptions>
{
  private applicationName: string = '';

  static isSupported() {
    return isSupported();
  }

  private checkApplicationNameName() {
    if (this.applicationName === '') {
      return Promise.reject(
        new PushNotificationsError(ERRORS.FCM_API_APPLICATIONNAME_NOT_FOUND)
      );
    }
  }

  private getFCMSubscription(
    options: FirebaseMessagingAPIOptions
  ): Promise<PushSubscriptionLike> {
    return swReady().then((registration) => {
      const app =
        getApps().filter((a) => a.name === this.applicationName)[0] ??
        initializeApp(
          {
            apiKey: options.apiKey,
            authDomain: options.authDomain,
            databaseURL: options.databaseURL,
            projectId: options.projectId,
            storageBucket: options.storageBucket,
            messagingSenderId: options.messagingSenderId,
            appId: options.appId,
          },
          this.applicationName
        );

      const messaging = getMessaging(app);

      // Подписываемся
      return getToken(messaging, {
        vapidKey: options.vapidKey,
        serviceWorkerRegistration: registration,
      })
        .then((token) => {
          if (!token) {
            return Promise.reject(
              new PushNotificationsError(ERRORS.FCM_API_RETURNED_EMPTY_TOKEN)
            );
          }

          return {
            endpoint: token,
            keys: {},
            unsubscribe: () => {
              return deleteToken(messaging) ?? Promise.resolve();
            },
          };
        })
        .catch((error) => {
          return Promise.reject(
            new PushNotificationsError({
              code: 'custom',
              message: `[FirebaseMessagingAPI]:${error}`,
            })
          );
        });
    });
  }

  public subscribe(
    options: FirebaseMessagingAPIOptions
  ): Promise<PushSubscriptionLike> {
    this.checkApplicationNameName();

    if (!options.force) {
      // Получаем браузерную подписку и смотрим, с каким ключом она создавалась
      // Если это был наш валидный vapid-ключ (из админки Firebase), значит всё ок
      // и вызов fcm.getToken(), скорее всего, не приведёт к повторной подписке или сетевому запросу.
      // В противном случае считаем что подписки нет.
      return swReady()
        .then((registration) => getSubscription(registration))
        .then((subscription) =>
          subscription ? describeSubscription(subscription) : null
        )
        .then((subscription) => {
          if (!subscription) {
            return Promise.reject(
              new PushNotificationsError(ERRORS.FCM_API_NOT_SUBSCRIBED)
            );
          }

          if (subscription.applicationServerKey !== options.vapidKey) {
            return Promise.reject(
              new PushNotificationsError(ERRORS.FCM_API_INVALID_KEY)
            );
          }

          return this.getFCMSubscription(options);
        });
    }

    // Всегда подписываемся в любом случае
    return this.getFCMSubscription(options);
  }

  public unsubscribe(
    options: Omit<FirebaseMessagingAPIOptions, 'force'>
  ): Promise<PushSubscriptionLike | void> {
    this.checkApplicationNameName();

    return (
      this.subscribe({ ...options, force: false })
        /**
         * .catch((error) => {})
         * Для сбора ошибок
         *
         * FirebaseMessagingAPI.unsubscribe', 'push error subscribe
         * push_pusherror_subscribe_in_unsubscribe
         */
        .then((subscription) => {
          return subscription
            ? subscription.unsubscribe().then(() => subscription)
            : Promise.resolve();
        })
    );
    /**
     * .catch((error) => {})
     * Для сбора ошибок
     *
     * FirebaseMessagingAPI.unsubscribe
     * push_pusherror_unsubscribe
     */
  }

  set appName(name: string) {
    this.applicationName = name;
  }
}
